<!--
 * @Author: AI Assistant
 * @Date: 2025-12-09
 * @Description: 滑动时长选择器组件（仅用于 Runway Gen 4/4.5）
 * @FilePath: /weryai-web/components/tool/DurationSlider.vue
-->
<template>
    <div ref="sliderWrapperRef" class="duration-slider-wrapper mt-5">
        <label class="flex items-center justify-between text-white text-sm mb-[10px]">
            <span>{{ t('tools.duration') }}</span>
            <span class="duration-current">{{ sliderValue }}s</span>
        </label>
        <div class="slider-container">
            <el-slider v-model="sliderValue" :min="min" :max="max" :step="step" :show-tooltip="false"
                :marks="marksWithUnit" :show-stops="true" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted, nextTick } from 'vue'
import { useI18n } from '../../composables/useI18n'

const props = withDefaults(
    defineProps<{
        min?: number
        max?: number
        step?: number
        defaultValue?: number
    }>(),
    {
        min: 2,
        max: 10,
        step: 1,
        defaultValue: 5,
    }
)

const { t } = useI18n()

const duration = defineModel<number | null>('duration')
const sliderWrapperRef = ref<HTMLElement | null>(null)

// 内部滑块值
const sliderValue = ref(props.defaultValue)

// 初始化
if (duration.value !== null && duration.value !== undefined) {
    sliderValue.value = duration.value
} else {
    duration.value = props.defaultValue
}

// 同步 sliderValue -> duration
watch(sliderValue, (val) => {
    duration.value = val
})

// 同步 duration -> sliderValue（外部修改时）
watch(duration, (val) => {
    if (val !== null && val !== undefined && val !== sliderValue.value) {
        sliderValue.value = val
    }
})

const MAX_FULL_MARK_COUNT = 10
const COMPACT_MARK_INTERVAL = 5

// 短区间显示全部文字；长区间只保留边界和 5 秒节点，滑动精度仍由 step 控制。
const marksWithUnit = computed(() => {
    const result: Record<number, string> = {}
    const markCount = Math.floor((props.max - props.min) / props.step) + 1

    if (markCount <= MAX_FULL_MARK_COUNT) {
        for (let i = props.min; i <= props.max; i += props.step) {
            result[i] = String(i)
        }
        return result
    }

    result[props.min] = String(props.min)
    const firstCompactMark =
        Math.ceil(props.min / COMPACT_MARK_INTERVAL) * COMPACT_MARK_INTERVAL
    for (
        let value = firstCompactMark;
        value < props.max;
        value += COMPACT_MARK_INTERVAL
    ) {
        if (
            value - props.min < COMPACT_MARK_INTERVAL / 2 ||
            props.max - value < COMPACT_MARK_INTERVAL / 2
        ) {
            continue
        }
        result[value] = String(value)
    }
    result[props.max] = String(props.max)
    return result
})

// 更新选中刻度的字体颜色
const updateMarkColors = () => {
    nextTick(() => {
        const container = sliderWrapperRef.value
        if (!container) return

        const marks = container.querySelectorAll('.el-slider__marks-text')
        marks.forEach((mark) => {
            const el = mark as HTMLElement
            const value = parseInt(el.textContent?.replace('s', '') || '0')
            if (value === sliderValue.value) {
                el.style.color = '#FFFFFF'
            } else {
                el.style.color = '#888'
            }
        })
    })
}

// 监听值变化更新颜色
watch(sliderValue, updateMarkColors)

onMounted(updateMarkColors)
</script>

<style scoped lang="less">
.duration-slider-wrapper {
    // padding: 0 12px 30px 12px;
    width: 100%;
}

.slider-container {
    position: relative;
    padding-bottom: 30px;
    z-index: 0;
    margin-left: 16px;
    margin-right: 8px;
}

.duration-current {
    color: #99f768;
    font-variant-numeric: tabular-nums;
}

// Element Plus Slider 样式覆盖
:deep(.el-slider) {
    --el-slider-main-bg-color: #99F768;
    --el-slider-runway-bg-color: #3a3d44;
    --el-slider-stop-bg-color: #555;
    --el-slider-disabled-color: #555;
    --el-slider-border-radius: 10px;
    --el-slider-height: 12px;
    --el-slider-button-size: 24px;
}

:deep(.el-slider__runway) {
    height: var(--el-slider-height);
    background-color: var(--el-slider-runway-bg-color);
    border-radius: var(--el-slider-border-radius);
}

:deep(.el-slider__bar) {
    height: var(--el-slider-height);
    background: #99F768;
    border-radius: var(--el-slider-border-radius);
    z-index: 1;
}

// 滑块按钮垂直居中
:deep(.el-slider__button-wrapper) {
    top: -13px;
}

:deep(.el-slider__button) {
    width: var(--el-slider-button-size);
    height: var(--el-slider-button-size);
    background-color: #99F768;
    border: 3px solid #fff;
    box-shadow: 0 0 0 4px rgba(153, 247, 104, 0.3);
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 0 0 6px rgba(153, 247, 104, 0.4);
    }
}

// 刻度标记样式
:deep(.el-slider__marks) {
    top: auto;
    bottom: -25px;
}

:deep(.el-slider__marks-text) {
    color: #888;
    font-size: 13px;
    font-weight: 500;
    margin-top: 25px;
    transition: color 0.2s ease;
}

// 刻度线（stops）- 未选中区域
:deep(.el-slider__stop) {
    width: 2px;
    height: 10px;
    background-color: #22242D;
    border-radius: 1px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 0;
}

</style>
